import { z } from 'zod';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  LiliNotification,
  LiliToastDefaultConfig,
  LiliNotificationType,
} from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import { throttle } from '@lili/utils/helpers';
import { OnboardingFormFields } from '@Types/index';
import { useFormHook, useSetError } from '@Hooks/index';
import { redirectToWebApp } from '@Services/utils/utils';
import { useCountdownTimer } from '@Hooks/useCountdownTimer';
import { useBoundStore } from '@Store/index';
import { useVerifyMfa } from './useVerifyMfa';
import { useSendResendLogin } from './useSendResendLogin';
import type { MFA_TYPES } from '@Types/index';
const phoneFormScheme = z.object({
  [OnboardingFormFields.MFA_OTP]: z.string(),
  [OnboardingFormFields.REMEMBER_ME]: z.boolean(),
});

const defaultValues = {
  [OnboardingFormFields.MFA_OTP]: '',
  [OnboardingFormFields.REMEMBER_ME]: false,
};

const otpLength = 6;

export const useAutoLoginPageLogic = (
  mfaType: MFA_TYPES,
  loginResponseCustomData: Record<string, unknown>
) => {
  const { t } = useTranslation(['inviteTeamMember']);

  const { asyncVerifyMfa, isLoadingVerifyMfa, verifyMfaResponse } = useVerifyMfa();
  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });
  const { asyncResendLoginMfa, isLoadingResendLoginMfa } = useSendResendLogin({
    onSuccess: () => {
      startTimer();
    },
    onError: () => {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={t('verifyPhone.resendCodeError')}
          id='resend-otp-failure'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    },
  });
  useEffect(() => {
    startTimer();
  }, []);
  const formMethods = useFormHook({
    formSchema: phoneFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'otp-phone-form',
    reValidateMode: 'onBlur',
  });
  const remmberMeValue = formMethods.watch();

  const onChange = throttle(async () => {
    const values = formMethods.getValues();

    if (values[OnboardingFormFields.MFA_OTP].length !== otpLength) return;
    if (isLoadingVerifyMfa) return;
    const { customerId } = await asyncVerifyMfa({
      answer: values[OnboardingFormFields.MFA_OTP],
      customData: loginResponseCustomData,
      mfaType,
      rememberMe: values[OnboardingFormFields.REMEMBER_ME],
    });

    redirectToWebApp({
      crid: customerId,
    });
  }, 300);

  const handleSendCode = async () => {
    const { customerId } = useBoundStore.getState().customer;
    try {
      if (!isLoadingResendLoginMfa)
        await asyncResendLoginMfa({
          customerId: customerId as string,
          loginResponseCustomData,
          mfaType,
          remmberMe: remmberMeValue[OnboardingFormFields.REMEMBER_ME],
          mfaDestination: loginResponseCustomData.mfaDestination as string,
        });
    } catch {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={t('verifyPhone.resendCodeError')}
          id='resend-otp-failure'
        />,
        { ...LiliToastDefaultConfig, closeOnClick: true }
      );
    }
  };
  useSetError({
    formMethods,
  });
  return {
    formMethods,

    asyncVerifyMfa,
    isLoadingVerifyMfa,
    verifyMfaResponse,
    onChange,
    otpLength,
    timeLeft,
    handleSendCode,
    isLoadingResendLoginMfa,
  };
};
